// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {buildGuildChannelTree, type ChannelTreeNode} from '@fluxer/schema/src/domains/channel/GuildChannelTree';

export const isTextChannel = (ch: Channel) =>
	ch.type === ChannelTypes.GUILD_TEXT ||
	ch.type === ChannelTypes.GUILD_ANNOUNCEMENT ||
	ch.type === ChannelTypes.GUILD_LINK ||
	ch.isThreadOnly();
export const isVoiceChannel = (ch: Channel) => ch.type === ChannelTypes.GUILD_VOICE;
export const isCategory = (ch: Channel) => ch.type === ChannelTypes.GUILD_CATEGORY;

/** One row of a group, in the one interleaved order: a channel the sidebar renders, or a nested category. */
export type ChannelGroupItem = {kind: 'channel'; channel: Channel} | {kind: 'category'; group: ChannelGroup};

/**
 * One category and everything directly inside it, in order.
 *
 * `category` is absent only for a synthetic root group: a run of parentless channels between two root
 * categories (or before the first, or after the last).
 */
export interface ChannelGroup {
	category?: Channel;
	/**
	 * LOCAL-ONLY: channels and subcategories interleaved in sibling order (position, then id), as the shared tree
	 * builder orders them (nested-ordering-plan.md 6.1). Types the sidebar does not render are left out.
	 */
	items: Array<ChannelGroupItem>;
}

/** The rendered channels directly inside a group, in order (nested categories excluded). */
export function groupChannels(group: ChannelGroup): Array<Channel> {
	const channels: Array<Channel> = [];
	for (const item of group.items) {
		if (item.kind === 'channel') channels.push(item.channel);
	}
	return channels;
}

/** The categories directly inside a group, in order. */
export function groupChildren(group: ChannelGroup): Array<ChannelGroup> {
	const children: Array<ChannelGroup> = [];
	for (const item of group.items) {
		if (item.kind === 'category') children.push(item.group);
	}
	return children;
}

/** LOCAL-ONLY: every rendered channel anywhere under a group, in sidebar (tree) order. */
export function subtreeChannelOrder(group: ChannelGroup): Array<string> {
	const ids: Array<string> = [];
	const walk = (current: ChannelGroup): void => {
		for (const item of current.items) {
			if (item.kind === 'category') walk(item.group);
			else ids.push(item.channel.id);
		}
	};
	walk(group);
	return ids;
}

/**
 * LOCAL-ONLY: rows in the order of `order` (a subtreeChannelOrder), so a collapsed category surfaces its selected,
 * unread and connected rows in sidebar order, text and voice mixed. Rows missing from `order` keep their place last.
 */
export function inTreeOrder<T extends {channel: {id: string}}>(
	order: ReadonlyArray<string>,
	rows: ReadonlyArray<T>,
): Array<T> {
	const index = new Map(order.map((id, i) => [id, i]));
	return rows
		.map((row, i) => ({row, i, at: index.get(row.channel.id) ?? Number.MAX_SAFE_INTEGER}))
		.sort((a, b) => a.at - b.at || a.i - b.i)
		.map((entry) => entry.row);
}

const isRenderedChannel = (channel: Channel) => isTextChannel(channel) || isVoiceChannel(channel);

/**
 * Builds the sidebar's channel tree from the shared builder (GuildChannelTree): one order at every depth, the
 * root included, so a channel can sit between two categories and a subcategory between two channels, exactly
 * where its stored position puts it. Siblings sort by (position, id as a snowflake); there is no type rank.
 *
 * No item is ever dropped. A `parentId` that resolves to nothing in this guild — a stale parent, a category the
 * client has not synced, a non-category — is treated as `null` and the item surfaces at root level rather than
 * disappearing. A cycle is broken the same way: the members stay reachable at root instead of nesting into
 * each other forever.
 *
 * The root's parentless channels are grouped into runs: each run of consecutive root channels becomes one
 * category-less group, placed between the root categories it sits between.
 */
export const organizeChannels = (channels: ReadonlyArray<Channel>): Array<ChannelGroup> => {
	const tree = buildGuildChannelTree<string, Channel>(channels);
	const toGroup = (node: ChannelTreeNode<string, Channel>): ChannelGroup => {
		const items: Array<ChannelGroupItem> = [];
		for (const child of node.children) {
			if (isCategory(child.channel)) {
				items.push({kind: 'category', group: toGroup(child)});
			} else if (isRenderedChannel(child.channel)) {
				items.push({kind: 'channel', channel: child.channel});
			}
		}
		return {category: node.channel, items};
	};
	const groups: Array<ChannelGroup> = [];
	let run: ChannelGroup | null = null;
	for (const root of tree.roots) {
		if (isCategory(root.channel)) {
			run = null;
			groups.push(toGroup(root));
			continue;
		}
		if (!isRenderedChannel(root.channel)) continue;
		if (!run) {
			run = {items: []};
			groups.push(run);
		}
		run.items.push({kind: 'channel', channel: root.channel});
	}
	return groups;
};
